import { useState, useEffect } from "react";
import { Check, Star, Zap } from "lucide-react";
import Wordmark from "../components/Wordmark";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

const OFFER_DEADLINE = new Date("2026-04-30T23:59:59+01:00");

function getOrCreateUserId() {
  let id = localStorage.getItem("yousift_user_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("yousift_user_id", id);
  }
  return id;
}

function useCountdown(target) {
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, target - Date.now()));

  useEffect(() => {
    const tick = () => setTimeLeft(Math.max(0, target - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);

  const total = Math.floor(timeLeft / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  return { days, hours, minutes, seconds, expired: timeLeft <= 0 };
}

function CountdownBlock({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-2xl font-black text-white tabular-nums w-10 text-center">
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-[10px] text-gray-500 uppercase tracking-widest">{label}</span>
    </div>
  );
}

function FeatureItem({ text }) {
  return (
    <li className="flex items-start gap-2 text-gray-300 text-sm">
      <Check size={15} className="text-[#FF0000] mt-0.5 shrink-0" />
      {text}
    </li>
  );
}

export default function PricingPage() {
  const { days, hours, minutes, seconds, expired } = useCountdown(OFFER_DEADLINE.getTime());
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState("");
  const userId = getOrCreateUserId();

  const handleCheckout = async (plan) => {
    setLoadingPlan(plan);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, user_id: userId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong.");
      window.location.href = data.url;
    } catch (e) {
      setError(e.message);
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F] flex flex-col">
      {/* Nav */}
      <header className="px-6 py-5 border-b border-[#1A1A1A] flex items-center justify-between">
        <a href="/">
          <Wordmark size="sm" darkBg={true} />
        </a>
        <a href="/" className="text-gray-400 hover:text-white text-sm transition-colors">
          ← Back
        </a>
      </header>

      <main className="flex-1 px-4 py-16 md:py-24">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-gray-500 mb-4">
            Pricing
          </span>
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
            Simple pricing. No surprises.
          </h1>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Start free. Upgrade when you're ready.
          </p>
        </div>

        {/* Cards */}
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 items-start">

          {/* Card 2 FIRST on mobile via order */}
          {/* ── CARD 2: Lifetime April (HERO) ── */}
          <div className="order-first md:order-2 relative flex flex-col bg-[#141414] border-2 border-[#FF0000] rounded-2xl p-7 shadow-[0_0_40px_rgba(255,0,0,0.15)] md:scale-105 md:-mt-4">
            {/* Badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="inline-flex items-center gap-1.5 bg-amber-400 text-black text-xs font-black px-4 py-1.5 rounded-full uppercase tracking-widest">
                <Star size={11} fill="black" /> Best Value
              </span>
            </div>

            <div className="mt-2 mb-1">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">Lifetime Access</p>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-4xl font-black text-white">£50</span>
                <span className="text-gray-500 line-through text-lg">£99</span>
              </div>
              <p className="text-gray-400 text-sm mb-5">
                Pay once. Use forever. Only available until 30th April.
              </p>
            </div>

            {/* Countdown */}
            {!expired ? (
              <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-xl p-4 mb-6">
                <p className="text-xs text-gray-500 uppercase tracking-widest text-center mb-3">
                  Price increases in:
                </p>
                <div className="flex items-center justify-center gap-3">
                  <CountdownBlock value={days} label="days" />
                  <span className="text-gray-600 font-bold mb-3">:</span>
                  <CountdownBlock value={hours} label="hrs" />
                  <span className="text-gray-600 font-bold mb-3">:</span>
                  <CountdownBlock value={minutes} label="min" />
                  <span className="text-gray-600 font-bold mb-3">:</span>
                  <CountdownBlock value={seconds} label="sec" />
                </div>
              </div>
            ) : (
              <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-4 mb-6 text-center">
                <p className="text-gray-400 text-sm">April offer has ended.</p>
              </div>
            )}

            <ul className="space-y-3 mb-8 flex-1">
              {[
                "Everything in Monthly",
                "Unlimited videos — forever",
                "All future features included",
                "Priority support",
                "Founding member status",
              ].map((f) => <FeatureItem key={f} text={f} />)}
            </ul>

            <button
              onClick={() => handleCheckout(expired ? "lifetime_standard" : "lifetime_april")}
              disabled={!!loadingPlan}
              className="w-full py-4 bg-[#FF0000] hover:bg-[#CC0000] disabled:opacity-50 text-white font-black text-base rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
            >
              {loadingPlan === "lifetime_april" || loadingPlan === "lifetime_standard" ? (
                <Spinner />
              ) : (
                <Zap size={16} />
              )}
              Claim Lifetime Access
            </button>
          </div>

          {/* ── CARD 1: Monthly ── */}
          <div className="order-2 md:order-1 flex flex-col bg-[#111] border border-[#2A2A2A] rounded-2xl p-7">
            <div className="mb-1">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">Monthly</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-black text-white">£19</span>
                <span className="text-gray-500 text-sm">/month</span>
              </div>
              <p className="text-gray-400 text-sm mb-6">
                Perfect for creators who want to start small and scale.
              </p>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              {[
                "Repurpose any YouTube video",
                "LinkedIn posts, blog articles & more",
                "Up to 10 videos per month",
                "Standard support",
              ].map((f) => <FeatureItem key={f} text={f} />)}
            </ul>

            <button
              onClick={() => handleCheckout("monthly")}
              disabled={!!loadingPlan}
              className="w-full py-4 bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-[#2A2A2A] hover:border-[#444] disabled:opacity-50 text-white font-bold text-base rounded-xl transition-all duration-200"
            >
              {loadingPlan === "monthly" ? <Spinner inline /> : "Get Started"}
            </button>
          </div>

          {/* ── CARD 3: Lifetime Standard ── */}
          <div className="order-3 md:order-3 flex flex-col bg-[#111] border border-[#2A2A2A] rounded-2xl p-7 opacity-75">
            <div className="mb-1">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">Lifetime Access</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-black text-white">£99</span>
                <span className="text-gray-500 text-sm">one-time</span>
              </div>
              <p className="text-gray-400 text-sm mb-6">
                The best way to use YouSift — pay once, own it forever.
              </p>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              {[
                "Everything in Monthly",
                "Unlimited videos — forever",
                "All future features included",
                "Priority support",
              ].map((f) => <FeatureItem key={f} text={f} />)}
            </ul>

            <button
              onClick={() => handleCheckout("lifetime_standard")}
              disabled={!!loadingPlan}
              className="w-full py-4 bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-[#2A2A2A] hover:border-[#444] disabled:opacity-50 text-white font-bold text-base rounded-xl transition-all duration-200"
            >
              {loadingPlan === "lifetime_standard" ? <Spinner inline /> : "Get Lifetime Access"}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <p className="text-center text-red-400 text-sm mt-6">{error}</p>
        )}

        {/* Guarantee */}
        <p className="text-center text-gray-600 text-sm mt-10">
          All plans include a{" "}
          <span className="text-gray-400 font-medium">7-day money-back guarantee</span>.
          No questions asked.
        </p>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center border-t border-[#1A1A1A]">
        <p className="text-gray-700 text-xs">
          &copy; {new Date().getFullYear()}{" "}
          <span className="text-gray-500 font-semibold">Bethany Labs</span> · All rights reserved
        </p>
      </footer>
    </div>
  );
}

function Spinner({ inline }) {
  return (
    <svg
      className={`animate-spin h-5 w-5 text-white ${inline ? "mx-auto" : ""}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
}
